import { get } from 'lodash';
import { getErrorMessage } from '../Components/Configuration/textConfiguration';
import { HasMessage, StandardAppConfig } from '../Components';
import {
    getDefaultAutoSuggestedAddressValidatorConfig,
    getDefaultManualAddressValidatorConfig,
    validateAddress
} from './addressValidator';
import { ImpactedAddressType, PersonalDisasterEligibility } from '../store';

export type PageValidatorContext = {
    formObject: object;
    pageName: string;
    appConfig: StandardAppConfig,
    errorSummary: Record<string, HasMessage>;
};

function isPositiveInteger(n) {
    return n >>> 0 === parseFloat(n);
}

// TS complains when String[].includes() is passed a non-string type
export function stringArrayIncludes(array: String[], item: any) {
    if (typeof item !== 'string') {
        return false;
    }
    return array.includes(item);
}

export const checkRequired = (context: PageValidatorContext, fieldPath: string) => {
    if (!get(context.formObject, fieldPath)) {
        // Avoid nesting errorSummary
        context.errorSummary[fieldPath] = {
            message: getErrorMessage(context.appConfig, 'eligibilityPage', fieldPath,
                'required', `[${fieldPath}] is required.`),
            errorType: 'Required'
        };
        return false;
    } else {
        return true;
    }
};

export const checkAddressAndSource = (
    context: PageValidatorContext,
    address: ImpactedAddressType | undefined): boolean => {

    const eligibility = context.formObject as PersonalDisasterEligibility;
    const sourceField = 'impactedAddressSource';
    const addressField = 'impactedAddress';
    const source = eligibility.impactedAddressSource || undefined;

    switch (source) {
        case 'residential':
            if (!checkResidentialAddress(address)) {
                setRequiredError(context, sourceField);
                return false;
            }
            return true;
        case 'other':
            if (!checkImpactedAddress(context, 'impactedAddress')) {
                setValidError(context, addressField);
                return false;
            }
            return true;
        case undefined:
            setRequiredError(context, sourceField);
            return false;
        default:
            assertUnreachable(source, 'Unknown source ');
    }
};

function assertUnreachable(obj: never, message: string): never {
    throw new Error(message + obj);
}

/** Validate address from profile connect. */
export const checkResidentialAddress = (residentialAddress: ImpactedAddressType | undefined): boolean =>
    !!residentialAddress?.formattedAddress;

/** Validate auto-suggested or manually input NswPoint address. */
export const checkImpactedAddress = (context: PageValidatorContext, fieldName: string): boolean => {
    const eligibility = context.formObject as PersonalDisasterEligibility;
    const addressMode = eligibility.impactedAddressMode;
    const errorConfig = (addressMode === 'auto') ? getDefaultAutoSuggestedAddressValidatorConfig() : getDefaultManualAddressValidatorConfig();
    const address = eligibility[fieldName] as ImpactedAddressType | undefined;
    return validateAddress(errorConfig, address);
};

const setRequiredError = (context: PageValidatorContext, fieldName: string) => {
    context.errorSummary[fieldName] = {
        message: getErrorMessage(context.appConfig, context.pageName, fieldName, 'required',
            `[${fieldName}] is required.`),
        errorType: 'Required'
    };
};

const setValidError = (context: PageValidatorContext, fieldName: string) => {
    context.errorSummary[fieldName] = {
        message: getErrorMessage(context.appConfig, context.pageName, fieldName, 'validAddress',
            `[${fieldName}] is must be a valid address.`),
        errorType: 'ValidAddress'
    };
};

export const checkRequiredYesNo = (context: PageValidatorContext, fieldPath: string) =>
    checkRequired(context, fieldPath) && checkYesNo(context, fieldPath);

export const checkYesNo = (context: PageValidatorContext, fieldPath: string) => {
    if (!stringArrayIncludes(['Yes', 'No'], get(context.formObject, fieldPath))) {
        context.errorSummary[fieldPath] = {
            message: getErrorMessage(context.appConfig, 'eligibilityPage', fieldPath,
                'NotInList', `[${fieldPath}] must be Yes or No.`),
            errorType: 'NotInList'
        };
        return false;
    } else {
        return true;
    }
};

export const checkInList = (context: PageValidatorContext, fieldPath: string, listOfValues: Array<string>) => {
    if (!stringArrayIncludes(listOfValues, get(context.formObject, fieldPath))) {
        context.errorSummary[fieldPath] = {
            message: getErrorMessage(context.appConfig, 'eligibilityPage', fieldPath,
                'NotInList', `For [${fieldPath}] your selection is not in the list of valid values: ${listOfValues.join(', ')}`),
            errorType: 'NotInList'
        };
        return false;
    } else {
        return true;
    }
};

export const checkYes = (context: PageValidatorContext, fieldPath: string) =>
    checkRequired(context, fieldPath) && checkInList(context, fieldPath, ['Yes']);

export const checkAtLeastOneEmployee = (context: PageValidatorContext, fieldPath: string) => {
    const value = get(context.formObject, fieldPath);

    if (!value && value !== 0) {
        return checkRequired(context, fieldPath);
    }

    if (!isPositiveInteger(value) || parseInt(value) === 0) {
        context.errorSummary[fieldPath] = {
            message: getErrorMessage(context.appConfig, 'eligibilityPage', fieldPath,
                'ValueType', `[${fieldPath}] must be a positive number.`),
            errorType: 'ValueType'
        };
        return false;
    } else {
        return true;
    }
};
